import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from '../../hooks/useTranslation';

// Animaciones
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.95); }
`;

const ripple = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
`;

const dots = keyframes`
  0%, 20% { color: rgba(230, 57, 70, 0.4); transform: scale(1); }
  50% { color: #e63946; transform: scale(1.3); }
  80%, 100% { color: rgba(230, 57, 70, 0.4); transform: scale(1); }
`;

// Contenedor principal
const SpinnerContainer = styled.div`
  ${props => props.$overlay && css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 9999;
  `}
  
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => props.$overlay ? '100vh' : '100px'};
  padding: 2rem;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  
  ${props => props.$overlay && css`
    background: ${props.theme === 'dark' 
      ? 'rgba(15, 23, 42, 0.95)' 
      : 'rgba(255, 255, 255, 0.95)'};
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid ${props.theme === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.1)'};
    border-radius: 20px;
    padding: 3rem 2rem;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 400px;
    text-align: center;
  `}
`;

// Spinner tipo círculo (default)
const CircleSpinner = styled.div`
  width: ${props => {
    const sizes = { small: '24px', medium: '40px', large: '60px', xl: '80px' };
    return sizes[props.$size] || sizes.medium;
  }};
  height: ${props => {
    const sizes = { small: '24px', medium: '40px', large: '60px', xl: '80px' };
    return sizes[props.$size] || sizes.medium;
  }};
  border: 3px solid transparent;
  border-top: 3px solid #e63946;
  border-right: 3px solid rgba(230, 57, 70, 0.3);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border-radius: 50%;
    border: 1px solid rgba(230, 57, 70, 0.1);
  }
`;

// Spinner tipo puntos
const DotsSpinner = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.div`
  width: ${props => {
    const sizes = { small: '8px', medium: '12px', large: '16px', xl: '20px' };
    return sizes[props.$size] || sizes.medium;
  }};
  height: ${props => {
    const sizes = { small: '8px', medium: '12px', large: '16px', xl: '20px' };
    return sizes[props.$size] || sizes.medium;
  }};
  background: #e63946;
  border-radius: 50%;
  animation: ${dots} 1.4s ease-in-out infinite both;
  
  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
`;

// Spinner tipo ondas
const RippleSpinner = styled.div`
  position: relative;
  width: ${props => {
    const sizes = { small: '32px', medium: '48px', large: '64px', xl: '80px' };
    return sizes[props.$size] || sizes.medium;
  }};
  height: ${props => {
    const sizes = { small: '32px', medium: '48px', large: '64px', xl: '80px' };
    return sizes[props.$size] || sizes.medium;
  }};
`;

const RippleRing = styled.div`
  position: absolute;
  border: 2px solid #e63946;
  opacity: 1;
  border-radius: 50%;
  animation: ${ripple} 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
  
  &:nth-child(2) {
    animation-delay: -0.5s;
  }
`;

// Spinner tipo pulso
const PulseSpinner = styled.div`
  width: ${props => {
    const sizes = { small: '24px', medium: '40px', large: '60px', xl: '80px' };
    return sizes[props.$size] || sizes.medium;
  }};
  height: ${props => {
    const sizes = { small: '24px', medium: '40px', large: '60px', xl: '80px' };
    return sizes[props.$size] || sizes.medium;
  }};
  background: linear-gradient(135deg, #e63946, #d52c3d);
  border-radius: 50%;
  animation: ${pulse} 1.5s ease-in-out infinite;
  box-shadow: 0 0 20px rgba(230, 57, 70, 0.4);
`;

// Mensaje
const SpinnerMessage = styled.p`
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#374151'};
  font-size: ${props => {
    const sizes = { small: '0.75rem', medium: '0.875rem', large: '1rem', xl: '1.125rem' };
    return sizes[props.$size] || sizes.medium;
  }};
  font-weight: 500;
  text-align: center;
  margin: 0;
  opacity: 0.8;
  letter-spacing: 0.025em;
  
  ${props => props.$overlay && css`
    color: ${props.theme === 'dark' ? '#f8fafc' : '#1f2937'};
    opacity: 1;
  `}
`;

// Submensaje opcional
const SpinnerSubtext = styled.p`
  color: ${props => props.theme === 'dark' ? '#94a3b8' : '#6b7280'};
  font-size: 0.75rem;
  text-align: center;
  margin: 0;
  opacity: 0.7;
  
  ${props => props.$overlay && css`
    color: ${props.theme === 'dark' ? '#cbd5e1' : '#4b5563'};
  `}
`;

const LoadingSpinner = ({ 
  size = 'medium', 
  type = 'circle',
  message = null, 
  subtext = '',
  overlay = false,
  theme = 'light',
  className = '' 
}) => {
  const { tCommon } = useTranslation();
  
  // Usar mensaje de traducción por defecto si no se proporciona uno
  const displayMessage = message !== null ? message : tCommon('loading');

  const renderSpinner = () => {
    switch (type) {
      case 'dots':
        return (
          <DotsSpinner>
            <Dot $size={size} />
            <Dot $size={size} />
            <Dot $size={size} />
          </DotsSpinner>
        );
      case 'ripple':
        return (
          <RippleSpinner $size={size}>
            <RippleRing />
            <RippleRing />
          </RippleSpinner>
        );
      case 'pulse':
        return <PulseSpinner $size={size} />;
      case 'circle':
      default:
        return <CircleSpinner $size={size} />;
    }
  };

  return (
    <SpinnerContainer $overlay={overlay} className={className}>
      <SpinnerWrapper $overlay={overlay} theme={theme}>
        {renderSpinner()}
        
        {displayMessage && (
          <SpinnerMessage $size={size} $overlay={overlay} theme={theme}>
            {displayMessage}
          </SpinnerMessage>
        )}
        
        {subtext && (
          <SpinnerSubtext $overlay={overlay} theme={theme}>
            {subtext}
          </SpinnerSubtext>
        )}
      </SpinnerWrapper>
    </SpinnerContainer>
  );
};

export default LoadingSpinner; 